import React, { useState, useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';
import EmailVerificationBanner from './components/EmailVerificationBanner';
import Landing from './pages/Landing';
import Dashboard from './pages/Dashboard';
import BookEditor from './pages/BookEditor';
import Illustrations from './pages/Illustrations';
import Credits from './pages/Credits';
import WritingStreak from './pages/WritingStreak';
import BookPromotion from './pages/BookPromotion';
import UltimateBookLaunch from './pages/UltimateBookLaunch';
import DeletedFiles from './pages/DeletedFiles';
import PaymentResult from './pages/PaymentResult';
import CoverDesigner from './pages/CoverDesigner';
import AdminDashboard from './pages/AdminDashboard';
import AffiliateDashboard from './pages/AffiliateDashboard';
import AffiliateLanding from './pages/AffiliateLanding';
import VideoManager from './pages/VideoManager';
import { Resources, Blog, Academy } from './pages/ContentPages';
import { ContactUs, PrivacyPolicy, TermsOfUse, CookieNotice } from './pages/LegalPages';
import './App.css';

// Theme context for dark mode
const ThemeContext = React.createContext();

export const useTheme = () => React.useContext(ThemeContext);

const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        const saved = localStorage.getItem('eskrybe_theme');
        return saved || 'light';
    });

    useEffect(() => {
        localStorage.setItem('eskrybe_theme', theme);
        document.documentElement.classList.toggle('dark', theme === 'dark');
    }, [theme]);

    const toggleTheme = () => setTheme(t => t === 'light' ? 'dark' : 'light');

    return (
        <ThemeContext.Provider value={{ theme, toggleTheme }}>
            {children}
        </ThemeContext.Provider>
    );
};

const AppContent = () => {
    const { isAuthenticated, loading, user, refreshUser, logout } = useAuth();
    const [currentPage, setCurrentPage] = useState('landing');
    const [selectedBook, setSelectedBook] = useState(null);
    const [verificationStatus, setVerificationStatus] = useState(null); // 'success', 'error', or null

    // Handle email verification token from URL (works for both auth and unauth users)
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const verifyToken = urlParams.get('verify');
        
        if (verifyToken) {
            // Process verification token
            const verifyEmail = async () => {
                try {
                    const API_URL = process.env.REACT_APP_BACKEND_URL;
                    const response = await fetch(`${API_URL}/api/auth/verify-email?token=${verifyToken}`, {
                        method: 'POST'
                    });
                    
                    if (response.ok) {
                        setVerificationStatus('success');
                        toast.success('Email verified successfully! Welcome to eSkrybe!');
                        // Clear URL and redirect to home
                        window.history.replaceState({}, document.title, '/');
                        // Refresh user state if logged in
                        if (isAuthenticated && refreshUser) {
                            refreshUser();
                        }
                    } else {
                        const data = await response.json();
                        setVerificationStatus('error');
                        toast.error(data.detail || 'Verification failed. Please try again.');
                        // Clear URL
                        window.history.replaceState({}, document.title, '/');
                    }
                } catch (error) {
                    setVerificationStatus('error');
                    toast.error('Verification failed. Please try again.');
                    console.error('Verification error:', error);
                    // Clear URL on error as well
                    window.history.replaceState({}, document.title, '/');
                }
            };
            verifyEmail();
        }
    }, [isAuthenticated, refreshUser]);

    // Check URL for payment result and path-based navigation on mount
    useEffect(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const sessionId = urlParams.get('session_id');
        const cancelled = urlParams.get('cancelled');
        const resetToken = urlParams.get('reset_password');
        const path = window.location.pathname;
        
        if (path === '/payment/success' && sessionId) {
            setCurrentPage('payment-result');
        } else if (path === '/payment/cancel' || cancelled) {
            setCurrentPage('payment-result');
        } else if (path === '/admin') {
            setCurrentPage('admin');
        } else if (path === '/affiliate') {
            setCurrentPage('affiliate');
        } else if (path === '/affiliate-dashboard') {
            setCurrentPage('affiliate-dashboard');
        } else if (resetToken) {
            // Password reset link - ensure we're on landing page for auth form
            setCurrentPage('landing');
        }
    }, []);

    const handleNavigate = (page) => {
        // Clear URL params when navigating away from payment
        if (currentPage === 'payment-result') {
            window.history.replaceState({}, '', '/');
        }
        setCurrentPage(page);
        if (page !== 'editor') {
            setSelectedBook(null);
        }
    };

    const handleSelectBook = (book) => {
        setSelectedBook(book);
        setCurrentPage('editor');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-background flex items-center justify-center">
                <div className="text-center">
                    <div className="w-8 h-8 border-2 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-4" />
                    <p className="text-muted-foreground">Loading...</p>
                </div>
            </div>
        );
    }

    // Route based on current page
    // Allow legal pages for unauthenticated users
    const publicPages = ['landing', 'privacy', 'terms', 'cookies', 'contact', 'affiliate', 'academy', 'admin', 'affiliate-dashboard', 'video-manager'];
    if (!isAuthenticated && !publicPages.includes(currentPage)) {
        return <Landing onNavigate={handleNavigate} />;
    }
    
    // Pages that don't require email verification (legal/public pages only)
    const skipVerificationPages = ['admin', 'video-manager', 'privacy', 'terms', 'cookies', 'contact', 'landing', 'affiliate'];
    
    // Handle start over - logout and show registration
    const handleStartOver = () => {
        logout();
        setCurrentPage('landing');
        // Trigger the auth modal to show registration after a brief delay
        setTimeout(() => {
            window.dispatchEvent(new CustomEvent('showAuthModal', { detail: { mode: 'register' } }));
        }, 100);
    };

    // STRICT: Block access for unverified users - show verification modal only
    // User MUST verify email before accessing any app features
    const requiresVerification = isAuthenticated && 
        user && 
        !user.email_verified && 
        !skipVerificationPages.includes(currentPage);
    
    if (requiresVerification) {
        return <EmailVerificationBanner user={user} onVerified={refreshUser} onStartOver={handleStartOver} />;
    }

    switch (currentPage) {
        case 'landing':
            return <Landing onNavigate={handleNavigate} />;
        
        case 'dashboard':
            return <Dashboard onNavigate={handleNavigate} onSelectBook={handleSelectBook} />;
        
        case 'editor':
            return selectedBook ? (
                <BookEditor book={selectedBook} onBack={() => handleNavigate('dashboard')} onNavigate={handleNavigate} />
            ) : (
                <Dashboard onNavigate={handleNavigate} onSelectBook={handleSelectBook} />
            );
        
        case 'illustrations':
            return <Illustrations onBack={() => handleNavigate('dashboard')} onNavigate={handleNavigate} />;
        
        case 'credits':
            return <Credits onBack={() => handleNavigate('dashboard')} />;
        
        case 'streak':
            return <WritingStreak onBack={() => handleNavigate('dashboard')} />;
        
        case 'promotion':
            return <BookPromotion onBack={() => handleNavigate('dashboard')} onNavigate={handleNavigate} />;
        
        case 'launch':
            return <UltimateBookLaunch onBack={() => handleNavigate('dashboard')} />;
        
        case 'resources':
            return <Resources onBack={() => handleNavigate('dashboard')} />;
        
        case 'blog':
            return <Blog onBack={() => handleNavigate(isAuthenticated ? 'dashboard' : 'landing')} />;
        
        case 'academy':
            return <Academy onBack={() => handleNavigate(isAuthenticated ? 'dashboard' : 'landing')} onNavigate={handleNavigate} />;
        
        case 'deleted':
            return <DeletedFiles onBack={() => handleNavigate('dashboard')} />;
        
        case 'covers':
            return <CoverDesigner onBack={() => handleNavigate('dashboard')} onNavigate={handleNavigate} />;
        
        case 'payment-result':
            return <PaymentResult onNavigate={handleNavigate} />;
        
        case 'contact':
            return <ContactUs onBack={() => handleNavigate(isAuthenticated ? 'dashboard' : 'landing')} />;
        
        case 'privacy':
            return <PrivacyPolicy onBack={() => handleNavigate(isAuthenticated ? 'dashboard' : 'landing')} />;
        
        case 'terms':
            return <TermsOfUse onBack={() => handleNavigate(isAuthenticated ? 'dashboard' : 'landing')} />;
        
        case 'cookies':
            return <CookieNotice onBack={() => handleNavigate(isAuthenticated ? 'dashboard' : 'landing')} />;
        
        case 'affiliate':
            return <AffiliateLanding onNavigate={handleNavigate} />;
        
        case 'admin':
            return <AdminDashboard onBack={() => handleNavigate(isAuthenticated ? 'dashboard' : 'landing')} onNavigate={handleNavigate} />;
        
        case 'video-manager':
            return <VideoManager onBack={() => handleNavigate('admin')} />;
        
        case 'affiliate-dashboard':
            return <AffiliateDashboard onBack={() => handleNavigate(isAuthenticated ? 'dashboard' : 'landing')} />;
        
        default:
            return <Landing onNavigate={handleNavigate} />;
    }
};

function App() {
    return (
        <ThemeProvider>
            <AuthProvider>
                <AppContent />
                <Toaster position="bottom-right" />
            </AuthProvider>
        </ThemeProvider>
    );
}

export default App;
